// WEBXR SPACE WARP, READY BY DETECTION (webxr-spacewarp w43, 2026-11-03; APP_SURFACE_PERF "Space warp",
// APP_PLAYER §VR "Space warp"). The Quest Browser's draft Layers addition (Meta: developers.meta.com/horizon/
// documentation/web/webxr-space-warp; spec: immersive-web.github.io/layers/#spacewarp): the app renders at HALF
// the display rate and hands the compositor a motion vector + depth image per frame; the compositor synthesizes
// the frame between (positional, not the rotation-only "hot air" of today's half rate). Meta documents it behind the
// browser flag "WebXR Space Warp", yet the stock Quest Browser GRANTS the optional request with the flag off (session
// 987; w54 headset: VrApi FPS=36/72, ASW=72 Type=App). A session
// that does not grant it makes this module do NOTHING (no GL call, no layer change, no band change).
// NOT REQUESTED BY DEFAULT (w52 vr-eyes-split, 2026-11-03): the owner's Quest Browser GRANTED the optional request
// (session 987: the SW home 0.85, 33-36 fps), and the first pass sampled the colour depth outside the colour
// viewport (below) — the eyes split by metres as soon as the head / rig moved. Until a headset proof of the
// corrected pass (work spacewarp-headset), only `?sw=1` asks for it.
//
//   swMode(search)            '?sw=1' on · else 'off' ('?sw=0' too)
//   swFeatures(on, search)    the OPTIONAL features the VR request adds (main.js enterAt): on the xr_multiview
//                             class only; on = ['space-warp', 'layers'] (Meta's documented pair), off = []
//   createSpaceWarp(renderer, { rig, log, mode }) -> the controller xrmv.js drives (createMultiview opts.sw):
//     .begin(session)         at the layer's creation: granted = the session's enabledFeatures has 'space-warp'
//                             (+ a half-float colour buffer); the `[xr] spacewarp ON / declined (reason)` line;
//                             granted → questprof.js setQuestSw (the SW band + MSAA, adaptres.js the 2-period target)
//     .granted                xrmv.js: the layer gets DEPTH_COMPONENT24, the colour pass keeps its OWN depth (under
//                             space-warp the layer's depth texture is motion-vector sized: spec "recommended motion
//                             vector texture resolution") and it is not invalidated (the pass reads it)
//     .frame(sub, layer)      view 0's sub-image each frame: motionVectorTexture / depthStencilTexture / their size
//                             (motionVectorTextureWidth/Height, the Quest's); reading them arms the warp that frame
//     .pass(depthTex)         after the frame's render (xrmv.js frameEnd): THE MOTION VECTOR PASS + the deltaPose
//     .end() / .state()
//
// THE PASS = DEPTH REPROJECTION, one full-screen multiview triangle at the motion vector size, no MSAA: per view
// Mreproj = VPprev · VPcur⁻¹ (float64 here: no world coordinate reaches the GPU), the colour pass's resolved depth d
// at the pixel → ndc = (uv·2−1, d·2−1), prev = Mreproj·ndc, MV = (ndc − prev.xyz/prev.w)·gain (cur − prev in NDC: Meta's
// sample cube-sea.js and Babylon's velocity shader; the gain: SW_MV_GAIN below), gl_FragDepth = d. It is the DRAWN depth: exact for every vertex
// displacement (ground geomorph, wind, the swap collapse, A2C cut-outs, the forest volume's gl_FragDepth) without a
// second scene walk. THE COLOUR VIEWPORT (w52): the MV + depth images cover the FULL view (spec §10: "to the full
// extent of those attachments, rather than to the viewport") while the colour pass draws into sub.viewport — the
// adaptres band's requestViewportScale rect (0.6–0.85 of the texture on the Quest): the MV pixel at uv reads the
// colour depth at uVp.xy + uv·uVp.zw (the viewport over the texture size), never at uv (that read the depth of
// another direction, or the cleared rest, and submitted it as the compositor's depth: each eye warped wrongly). The world is static and its light baked: the camera's motion is all of its motion. Not covered:
// things that move on their own in the WORLD (lift cabins, other players) get the static MV (they warp as if fixed:
// a residual judder at their own speed, the half-rate look they have today); translucent world draws (water if
// blended, beam) carry the depth behind them — the compositor warps them with the background.
// THE RIG'S OWN MESHES (w54): the HUD, menu panels, held weapon, controllers ride the head / the rig, so the static MV
// slid them with the world in every synthesized frame and the next real frame put them back ("two pictures when
// moving"). After the reprojection swlocked.js draws them again with their OWN prev / cur matrices (MV 0 for the
// head-locked HUD) — see its header for the rules.
//
// deltaPose (spec: "the incremental application-applied transform since the previous frame"; Meta's sample
// proj-spacewarp.html: prevRigInWorld⁻¹ · curRigInWorld, null when unchanged): the rig's world matrix (walk, beam,
// jet, bike, lifts move the rig; the head stays the session's). A jump over CUT_M (a beam) or a new reference space
// (⟲ Recenter: the rig turns WITH the origin, the view does not move) cuts: no deltaPose, MV from this frame only.
import * as THREE from 'three';
import { setQuestSw } from './questprof.js';
import { createLockedPass } from './swlocked.js';

export const SW_FEATURE = 'space-warp';
const VIEWS = 2;
const CUT_M = 10;          // a rig jump over this in one frame is a cut (the beam), not motion

// THE MOTION VECTOR GAIN (w54, headset-measured): the Quest Browser's compositor moved a synthesized frame by only
// 0.26 (±0.01, 4 runs, 2 turn rates) of the real frame-to-frame step for MV = cur − prev NDC (Meta's definition; the
// screen capture's synthesized frames, scaled linearly: gain 1.74 → 0.45, 2 → 0.52, 2.5 → 0.65). At 0.26 the
// display steps 0, 0.26, 1, 1.26 … — an uneven 36 Hz cadence the eye pursues as TWO pictures of the moving world.
// ×2 lands it halfway (the even 72 Hz). `?swg=<0.5..4>` overrides it (the owner's A/B; ?swg=1 = Meta's raw MV).
export const SW_MV_GAIN = 2;
export function swGain(search = (typeof location !== 'undefined' ? location.search : '')) {
  let v = null;
  try { v = new URLSearchParams(search || '').get('swg'); } catch { v = null; }
  const n = v == null || v === '' ? NaN : Number(v);
  return Number.isFinite(n) && n >= 0.5 && n <= 4 ? n : SW_MV_GAIN;
}

export function swMode(search = (typeof location !== 'undefined' ? location.search : '')) {
  let v = null;
  try { v = new URLSearchParams(search || '').get('sw'); } catch { v = null; }
  return v === '1' ? 'on' : 'off';
}

// the optional features a VR request adds (main.js enterAt: on = the xr_multiview class)
export function swFeatures(on, search) {
  if (!on) return [];
  return swMode(search) === 'on' ? [SW_FEATURE, 'layers'] : [];
}

const VS = `#version 300 es
#extension GL_OVR_multiview2 : require
layout(num_views = 2) in;
out vec2 vUv;
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  vUv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;
const FS = `#version 300 es
#extension GL_OVR_multiview2 : require
precision highp float;
precision highp sampler2DArray;
uniform sampler2DArray uDepth;
uniform mat4 uReproj[2];
uniform vec4 uVp;
uniform float uGain;
in vec2 vUv;
layout(location = 0) out vec4 oMv;
void main() {
  int v = int(gl_ViewID_OVR);
  float d = texture(uDepth, vec3(uVp.xy + vUv * uVp.zw, float(v))).r;
  vec4 ndc = vec4(vUv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
  vec4 pc = uReproj[v] * ndc;
  oMv = vec4((ndc.xyz - pc.xyz / pc.w) * uGain, 0.0);
  gl_FragDepth = d;
}`;

export function createSpaceWarp(renderer, { rig = null, log = console.info, mode = swMode(), gain = swGain() } = {}) {
  const gl = renderer.getContext();
  let g = null;                 // { prog, uDepth, uReproj, fb, vao } the pass's GL objects (made at the first pass)
  let sub = null, layer = null, refSpace = null;
  const vp = new Float32Array([0, 0, 1, 1]);   // the colour viewport over the colour texture (uVp)
  let facts = { mode, gain, requested: mode !== 'off', granted: false, reason: 'no session', frames: 0, passes: 0, cuts: 0, mv: null, ms: null };
  const prevVP = [new THREE.Matrix4(), new THREE.Matrix4()], curVP = [new THREE.Matrix4(), new THREE.Matrix4()];
  const lastVP = [new THREE.Matrix4(), new THREE.Matrix4()];   // the previous frame's VP (the locked pass's prev)
  const locked = createLockedPass(gl, renderer);
  let wasCut = false;
  const inv = new THREE.Matrix4(), M = new THREE.Matrix4(), reproj = new Float32Array(16 * VIEWS);
  const rigPrev = new THREE.Matrix4(), rigCur = new THREE.Matrix4(), dM = new THREE.Matrix4();
  const dp = new THREE.Vector3(), dq = new THREE.Quaternion(), ds = new THREE.Vector3();
  let havePrev = false, msSum = 0, msN = 0, said = false;

  const decline = (why) => {
    c.granted = false; facts.granted = false; facts.reason = why;
    setQuestSw(false);
    log(`[xr] spacewarp declined (${why})`);
  };

  const c = {
    granted: false,
    begin(session) {
      facts = { ...facts, granted: false, frames: 0, passes: 0, cuts: 0, mv: null, ms: null };
      havePrev = false; said = false; msSum = 0; msN = 0; sub = null; layer = null; refSpace = null;
      const ef = session && session.enabledFeatures;
      if (mode === 'off') { facts.reason = 'off (not requested; ?sw=1 asks)'; c.granted = false; return false; }
      if (!Array.isArray(ef) && !(ef && typeof ef.includes === 'function')) { decline('enabledFeatures unknown'); return false; }
      if (!ef.includes(SW_FEATURE)) { decline(`not granted (requested space-warp + layers; the browser flag "WebXR Space Warp" off?)`); return false; }
      if (!gl.getExtension('EXT_color_buffer_half_float') && !gl.getExtension('EXT_color_buffer_float')) { decline('no EXT_color_buffer_half_float'); return false; }
      c.granted = true; facts.granted = true; facts.reason = 'granted';
      setQuestSw(true);
      log(`[xr] spacewarp ON (granted; mode ${mode}; MV gain ${gain}; features ${[...ef].join(',')})`);
      return true;
    },

    // view 0 of each frame (xrmv.js frame): the sub-image's motion vector + depth textures (reading them arms it)
    frame(s, l) {
      if (!c.granted) return;
      const mv = s && s.motionVectorTexture, d = s && s.depthStencilTexture;
      const w = s && s.motionVectorTextureWidth, h = s && s.motionVectorTextureHeight;
      if (!mv || !d || !(w > 0) || !(h > 0)) { decline(`the sub-image has no motionVectorTexture / depthStencilTexture / size (${!!mv}/${!!d}/${w}×${h})`); return; }
      sub = s; layer = l;
      facts.mv = { w, h };
      // the rect the colour pass drew this frame (adaptres requestViewportScale), over the texture the depth has
      const v = s.viewport, tw = l.textureWidth, th = l.textureHeight;
      if (v && tw > 0 && th > 0) { vp[0] = v.x / tw; vp[1] = v.y / th; vp[2] = v.width / tw; vp[3] = v.height / th; }
      else { vp[0] = 0; vp[1] = 0; vp[2] = 1; vp[3] = 1; }
      facts.vp = Array.from(vp, (x) => +x.toFixed(4));
      if (!said) { said = true; log(`[xr] spacewarp motion vectors ${w}×${h} (colour ${l.textureWidth}×${l.textureHeight}), ignoreDepthValues ${l.ignoreDepthValues}`); }
    },

    // after the frame's render (xrmv.js frameEnd): the motion vector pass into the sub-image + the deltaPose
    pass(depthTex) {
      if (!c.granted || !sub || !depthTex) return;
      const t0 = performance.now();
      facts.frames++;
      const xc = renderer.xr.getCamera();
      const cams = xc && xc.cameras;
      if (!cams || cams.length < VIEWS) return;
      // the rig (walk / beam / jet / bike / lift) and the reference space (⟲ Recenter)
      let cut = !havePrev;
      const rs = renderer.xr.getReferenceSpace ? renderer.xr.getReferenceSpace() : null;
      if (rig) { rig.updateWorldMatrix(true, false); rigCur.copy(rig.matrixWorld); }
      let delta = null;
      if (havePrev && rig) {
        dM.copy(rigPrev).invert().multiply(rigCur);
        dM.decompose(dp, dq, ds);
        if (dp.length() > CUT_M) cut = true;
        else if (rs === refSpace && (dp.lengthSq() > 1e-12 || Math.abs(dq.w) < 1 - 1e-12)) delta = { p: dp, q: dq };
      }
      if (layer && 'deltaPose' in layer && typeof XRRigidTransform !== 'undefined') {
        try {
          layer.deltaPose = delta && !cut ? new XRRigidTransform({ x: delta.p.x, y: delta.p.y, z: delta.p.z }, { x: delta.q.x, y: delta.q.y, z: delta.q.z, w: delta.q.w }) : null;
        } catch { /* a runtime without it: the MV alone */ }
      }
      refSpace = rs;
      if (rig) rigPrev.copy(rigCur);
      // this frame's view-projections; a cut reprojects onto itself (MV 0)
      for (let v = 0; v < VIEWS; v++) {
        curVP[v].multiplyMatrices(cams[v].projectionMatrix, cams[v].matrixWorldInverse);
        if (cut) prevVP[v].copy(curVP[v]);
        lastVP[v].copy(prevVP[v]);
        M.multiplyMatrices(prevVP[v], inv.copy(curVP[v]).invert());
        reproj.set(M.elements, 16 * v);
        prevVP[v].copy(curVP[v]);
      }
      if (cut) facts.cuts++;
      havePrev = true;
      wasCut = cut;
      draw(depthTex, cams);
      facts.passes++;
      msSum += performance.now() - t0; msN++;
      facts.ms = +(msSum / msN).toFixed(3);
    },

    end() {
      if (g) { gl.deleteFramebuffer(g.fb); gl.deleteProgram(g.prog); gl.deleteVertexArray(g.vao); g = null; }
      locked.dispose();
      if (c.granted) log(`[xr] spacewarp session end: ${facts.passes} passes, ${facts.cuts} cuts, CPU ${facts.ms} ms a pass`);
      c.granted = false; sub = null; layer = null;
      setQuestSw(false);
    },

    state() { return { ...facts, locked: { ...locked.facts } }; },
  };

  function prog() {
    const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); return o; };
    const p = gl.createProgram(), vs = sh(gl.VERTEX_SHADER, VS), fs = sh(gl.FRAGMENT_SHADER, FS);
    gl.attachShader(p, vs); gl.attachShader(p, fs); gl.linkProgram(p);
    gl.deleteShader(vs); gl.deleteShader(fs);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      const why = (gl.getProgramInfoLog(p) || '').slice(0, 200);
      gl.deleteProgram(p);
      return { err: why || 'link failed' };
    }
    return { prog: p, uDepth: gl.getUniformLocation(p, 'uDepth'), uReproj: gl.getUniformLocation(p, 'uReproj[0]'), uVp: gl.getUniformLocation(p, 'uVp'), uGain: gl.getUniformLocation(p, 'uGain'), fb: gl.createFramebuffer(), vao: gl.createVertexArray() };
  }

  // raw GL, then three's state cache reset (the painters' pattern; xr-rebind re-binds the next render's target)
  function draw(depthTex, cams) {
    if (!g) { g = prog(); if (g.err) { const e = g.err; g = null; decline(`the motion vector program: ${e}`); return; } }
    const ext = gl.getExtension('OVR_multiview2');
    const { w, h } = facts.mv;
    gl.bindFramebuffer(gl.FRAMEBUFFER, g.fb);
    // no MSAA on the motion vectors (Meta: "values that don't make sense")
    ext.framebufferTextureMultiviewOVR(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, sub.motionVectorTexture, 0, 0, VIEWS);
    ext.framebufferTextureMultiviewOVR(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, sub.depthStencilTexture, 0, 0, VIEWS);
    gl.viewport(0, 0, w, h);
    gl.disable(gl.SCISSOR_TEST); gl.disable(gl.BLEND); gl.disable(gl.CULL_FACE); gl.disable(gl.SAMPLE_ALPHA_TO_COVERAGE);
    gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.ALWAYS); gl.depthMask(true); gl.colorMask(true, true, true, true);
    gl.useProgram(g.prog);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, depthTex);
    gl.uniform1i(g.uDepth, 0);
    gl.uniformMatrix4fv(g.uReproj, false, reproj);
    gl.uniform4fv(g.uVp, vp);
    gl.uniform1f(g.uGain, gain);
    gl.bindVertexArray(g.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
    // the rig's own meshes (HUD, panels, weapon, controllers) with their own motion over the reprojection (swlocked.js)
    locked.draw(rig, cams, curVP, lastVP, wasCut, gain);
    renderer.resetState();
  }

  return c;
}
